import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CircleAlert, FileUp, Search, ShieldCheck, MailCheck, Minus, Plus, Users,
} from 'lucide-react';
import {
  ComingSoonButton,
  Field, OtpInput, PasswordInput, PhoneInput, ResendCooldown, Stepper,
  Notice,
  useToast,
  type PasswordRule,
} from '@kollez/design-system';
import { apiPost } from '@/lib/api';
import { useAuthStore, type AuthUser, type Membership } from '@/lib/authStore';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker';
import { ClaimOtpsStep } from '@/components/ClaimOtpsStep';

/**
 * /register/student (PR 4b) — rework of the wizard to route students through the three
 * onboarding paths defined in the multi-tenant identity spec:
 *
 *   Persona 1 (unaffiliated) → Kollez Public only. Immediate login.
 *   Path 2 (self-declared + no shell match, OR cold-declare via AISHE) → pending Membership,
 *          TPO approves later. Immediate login on Kollez Public.
 *   Path 3 (declared tenant + roster match) → server returns { requiresClaimOtpVerification:
 *          true, claimAttemptId, ... }; step 5 shows the ClaimOtpsStep. Student verifies both
 *          OTPs; server claims the shell and hydrates the session.
 *
 * Wizard steps:
 *   1. You             — name, email, mobile+OTP (Tier 1), password, DOB, parent for minors.
 *   2. Where you study — affiliation radio + TenantPicker (or skip for unaffiliated).
 *   3. Academics       — program, branch, joining year, roll, unique-ID prompt (Path 3).
 *   4. Review          — summary + DPDP consent → submit.
 *   5. Done            — either "check email" success card OR the ClaimOtpsStep for Path 3.
 */

const PW_RULES: PasswordRule[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'Includes a number', test: (p) => /\d/.test(p) },
];

type Affiliation = 'currently_studying' | 'graduated' | 'unaffiliated';

const CURRENT_YEAR = new Date().getFullYear();
const JOINING_YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);
const PROGRAMS = ['B.Tech', 'M.Tech', 'MBA', 'MCA', 'B.Sc', 'BBA', 'B.Com', 'M.Sc', 'PhD'];

interface RegisterSuccess {
  user: AuthUser;
  memberships: Membership[];
  activeTenantId: string | null;
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
}

interface RegisterPath3Pending {
  user: { id: string; fullName: string; email: string; role: string };
  requiresClaimOtpVerification: true;
  claimAttemptId: string;
  expiresInSec: number;
  otpsForDev?: { emailOtp?: string; mobileOtp?: string };
  message?: string;
}

type RegisterResponse = RegisterSuccess | RegisterPath3Pending;

function isPath3Pending(res: RegisterResponse): res is RegisterPath3Pending {
  return (res as RegisterPath3Pending).requiresClaimOtpVerification === true;
}

export function RegisterStudentPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const setSession = useAuthStore((s) => s.setSession);

  const [step, setStep] = useState(1);
  const max = 4;

  // Step 1
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [dob, setDob] = useState('');
  const [parentMobile, setParentMobile] = useState('');

  // Step 2
  const [affiliation, setAffiliation] = useState<Affiliation>('currently_studying');
  const [picked, setPicked] = useState<TenantPickerValue | null>(null);

  // Step 3
  const [program, setProgram] = useState('B.Tech');
  const [branch, setBranch] = useState('Computer Science (CSE)');
  const [joiningYear, setJoiningYear] = useState<number>(CURRENT_YEAR);
  const [roll, setRoll] = useState('');
  const [uniqueId, setUniqueId] = useState('');
  const [cgpa, setCgpa] = useState('');
  const [backlogs, setBacklogs] = useState(0);
  const [tenth, setTenth] = useState('');
  const [twelfth, setTwelfth] = useState('');

  // Step 4
  const [consent, setConsent] = useState(false);

  // Step 5 (Path 3)
  const [path3, setPath3] = useState<RegisterPath3Pending | null>(null);
  // Success card for Persona 1 / Path 2 flows (no roster OTP challenge).
  const [immediateSuccess, setImmediateSuccess] = useState<RegisterSuccess | null>(null);

  const [submitting, setSubmitting] = useState(false);

  const age = useMemo(() => {
    if (!dob) return 20;
    return (Date.now() - new Date(dob).getTime()) / 31557600000;
  }, [dob]);
  const isMinor = age < 18;

  // If someone passes ?tenant=<id> we could deep-link, but for now start clean.
  useEffect(() => { void params; }, [params]);

  // If the student typed a roll number, mirror it into the unique-id field by default.
  useEffect(() => {
    if (roll && !uniqueId) setUniqueId(roll);
  }, [roll, uniqueId]);

  // Compute current academic year for display.
  function currentYearFrom(join: number): number {
    const now = new Date();
    const cutoffMonth = 7;
    return Math.max(1, now.getFullYear() - join + (now.getMonth() + 1 >= cutoffMonth ? 1 : 0));
  }
  const acYear = currentYearFrom(joiningYear);

  const skipAcademics = affiliation === 'unaffiliated';

  function next() {
    if (step === 1) {
      if (!name.trim() || !email.trim() || mobile.length !== 10 || password.length < 8) {
        toast.show({ tone: 'warning', message: 'Fill everything and set a password (8+ chars, a number).' });
        return;
      }
      if (isMinor && !parentMobile) {
        toast.show({ tone: 'warning', message: 'DPDP §6 requires a parent or guardian mobile for under-18 signups.' });
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (affiliation !== 'unaffiliated' && !picked) {
        toast.show({ tone: 'warning', message: 'Pick your college, or choose "Not affiliated" above.' });
        return;
      }
      // Persona 1 skips academics — jump straight to review.
      setStep(skipAcademics ? 4 : 3);
      return;
    }
    if (step === 3) {
      setStep(4);
      return;
    }
    if (step === max) {
      if (!consent) {
        toast.show({ tone: 'warning', message: 'Please agree to the DPDP consent before finishing.' });
        return;
      }
      void submitAll();
      return;
    }
  }

  function back() {
    if (step === 4 && skipAcademics) {
      setStep(2);
      return;
    }
    if (step > 1) setStep(step - 1);
  }

  async function submitAll(overrides?: { forcePath2?: boolean }) {
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        fullName: name,
        email,
        phone: mobile,
        password,
        program: skipAcademics ? 'Unaffiliated' : program,
        branch: skipAcademics ? 'Unaffiliated' : branch,
        specialization: skipAcademics ? undefined : branch,
        joiningYear: skipAcademics ? undefined : joiningYear,
        rollNumber: skipAcademics || !roll ? undefined : roll,
        cgpa: cgpa ? Number(cgpa) : undefined,
        backlogs: skipAcademics ? undefined : backlogs,
        class10: tenth ? Number(tenth) : undefined,
        class12: twelfth ? Number(twelfth) : undefined,
        skills: [],
        interestedRoles: [],
        preferredLocations: [],
        consent,
        dateOfBirth: dob || undefined,
        ...(isMinor && parentMobile ? { parentGuardian: { mobile: parentMobile } } : {}),
      };

      // Wire the tenant selection to the appropriate id field.
      if (picked?.source === 'kollez' && picked.tenantId) {
        payload.declaredTenantId = picked.tenantId;
      } else if (picked?.source === 'aishe' && picked.aisheCollegeId) {
        payload.aisheCollegeId = picked.aisheCollegeId;
      }

      // Path 3 hint — send only when we have both a Kollez tenant AND an identifier, and
      // the caller isn't forcing the manual fallback.
      if (
        !overrides?.forcePath2
        && picked?.source === 'kollez'
        && uniqueId.trim()
      ) {
        payload.uniqueIdentifierForShellMatch = uniqueId.trim();
      }

      const res = await apiPost<RegisterResponse>('/auth/register/student', payload);

      if (isPath3Pending(res)) {
        setPath3(res);
      } else {
        setSession(res.user, res.tokens, {
          activeTenantId: res.activeTenantId,
          memberships: res.memberships,
        });
        setImmediateSuccess(res);
      }
      setStep(5);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Could not create account.';
      toast.show({ tone: 'warning', message: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main id="main" className="mx-auto flex-1 w-full" style={{ maxWidth: 640, padding: '32px 20px 120px' }}>
        {/* Stepper — only during the wizard, not on the done step */}
        {step <= max && (
          <Stepper
            ariaLabel="Sign-up progress"
            current={step}
            steps={[
              { label: 'You' },
              { label: 'Where' },
              { label: 'Academics' },
              { label: 'Review' },
            ]}
          />
        )}

        <div className="mt-8">
          {/* STEP 1 — Tier 1 auth */}
          {step === 1 && (
            <section className="grid gap-5">
              <div>
                <h1 className="h-page">Tell us who you are</h1>
                <p className="subhead mt-2">Use your name exactly as it appears on your marksheet — companies check.</p>
              </div>
              <Field label="Full name">
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Meghana Varma" required />
              </Field>
              <Field label="Email" helper="Offer letters and interview invites go here.">
                <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@gmail.com" required />
              </Field>
              <div>
                <label className="label">Mobile number</label>
                <div className="flex gap-2 mt-1.5">
                  <PhoneInput
                    wrapperClassName="flex-1"
                    value={mobile}
                    onChange={setMobile}
                    placeholder="98765 43210"
                  />
                  <button type="button" className="btn btn-outline" onClick={() => setOtpSent(true)} disabled={otpSent || mobile.length !== 10}>{otpSent ? 'Sent' : 'Send code'}</button>
                </div>
                {otpSent && (
                  <div className="mt-3 grid gap-2">
                    <label className="label">Enter the 6-digit code sent by SMS</label>
                    <OtpInput value={otp} onChange={setOtp} />
                    <p className="helper mt-1 m-0">
                      Didn&apos;t get it?{' '}
                      <ResendCooldown seconds={30} onResend={() => setOtpSent(true)} />
                    </p>
                  </div>
                )}
              </div>
              <Field label="Create a password">
                <PasswordInput
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  rules={PW_RULES}
                />
              </Field>
              <Field label="Date of birth">
                <input className="input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} style={{ maxWidth: 220 }} />
              </Field>
              {isMinor && (
                <div className="notice notice-info">
                  <Users size={16} />
                  <div className="grid gap-2 flex-1">
                    <span>You&apos;re under 18, so India&apos;s DPDP Act needs a parent or guardian to agree before we store your details.</span>
                    <div className="flex gap-2 flex-wrap">
                      <PhoneInput
                        wrapperClassName="flex-1"
                        value={parentMobile}
                        onChange={setParentMobile}
                        placeholder="Parent's mobile"
                      />
                      <ComingSoonButton
                        label="Send consent request"
                        reason="Consent flow launches when SMS transport lands."
                        variant="outline"
                        size="sm"
                        showHelper={false}
                      />
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* STEP 2 — affiliation + tenant picker */}
          {step === 2 && (
            <section className="grid gap-5">
              <div>
                <h1 className="h-page">Where do you study?</h1>
                <p className="subhead mt-2">
                  Kollez uses this to show you the right drives. You&apos;ll always have Kollez Public
                  as your public workspace too.
                </p>
              </div>

              <div className="grid gap-2">
                <span className="label">Are you at a college?</span>
                <div className="grid gap-2">
                  {[
                    { v: 'currently_studying', label: 'Yes, currently studying', hint: 'You\'ll pick your college next.' },
                    { v: 'graduated', label: 'I\'ve graduated', hint: 'You can still browse drives and use Kollez Public.' },
                    { v: 'unaffiliated', label: 'Not affiliated with any college', hint: 'You\'ll use Kollez Public only — that\'s fine.' },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      className={`card card-pad flex gap-3 items-start cursor-pointer ${affiliation === opt.v ? 'border-accent' : ''}`}
                      style={{
                        borderWidth: affiliation === opt.v ? 2 : 1,
                        borderColor: affiliation === opt.v ? 'rgb(var(--ink-accent))' : undefined,
                      }}
                    >
                      <input
                        type="radio"
                        name="affiliation"
                        value={opt.v}
                        checked={affiliation === opt.v}
                        onChange={() => { setAffiliation(opt.v as Affiliation); if (opt.v === 'unaffiliated') setPicked(null); }}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-sm text-ink-secondary">{opt.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {affiliation !== 'unaffiliated' && (
                <div>
                  <label className="label">Your college</label>
                  <TenantPicker value={picked} onPick={setPicked} />
                </div>
              )}
            </section>
          )}

          {/* STEP 3 — academics (skipped for unaffiliated) */}
          {step === 3 && !skipAcademics && (
            <section className="grid gap-5">
              <div>
                <h1 className="h-page">Your academics</h1>
                <p className="subhead mt-2">Your college verifies these before you can apply. Eligibility for each drive is worked out from them.</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Program</label>
                  <select className="select" value={program} onChange={(e) => setProgram(e.target.value)}>
                    {PROGRAMS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Branch / specialization</label>
                  <input className="input" value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="e.g. Computer Science (CSE)" />
                </div>
              </div>
              <div>
                <label className="label">Joining year</label>
                <select className="select" value={joiningYear} onChange={(e) => setJoiningYear(Number(e.target.value))} style={{ maxWidth: 220 }}>
                  {JOINING_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <p className="helper mt-1.5">
                  You&apos;re in your <strong>Year {acYear}</strong>. Kollez computes this at read time so joining year stays authoritative.
                </p>
              </div>
              <div>
                <label className="label">Roll / hall ticket number <span className="opt">(optional)</span></label>
                <input className="input uppercase" value={roll} onChange={(e) => setRoll(e.target.value.toUpperCase())} placeholder="22A91A0417" />
                <p className="helper mt-1.5">
                  Optional but strongly encouraged — helps your college verify you automatically.
                  Without it, your college&apos;s TPO will manually verify your profile after signup.
                </p>
              </div>
              {picked?.source === 'kollez' && (
                <div>
                  <label className="label">Do you know your student ID, college-issued email, or the mobile your college has on file?</label>
                  <input
                    className="input"
                    value={uniqueId}
                    onChange={(e) => setUniqueId(e.target.value)}
                    placeholder="Roll number, college email, or mobile"
                  />
                  <p className="helper mt-1.5">
                    If we find a match in your college&apos;s roster, we&apos;ll send OTPs to the email
                    and mobile they have on file to activate your account instantly. If not, your TPO
                    will verify you manually.
                  </p>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Current CGPA <span className="opt">(optional)</span></label>
                  <input className="input" inputMode="decimal" placeholder="8.42" value={cgpa} onChange={(e) => setCgpa(e.target.value)} />
                </div>
                <div>
                  <label className="label">Active backlogs</label>
                  <div className="flex items-center gap-2">
                    <button type="button" className="btn btn-outline btn-icon" onClick={() => setBacklogs((n) => Math.max(0, n - 1))} aria-label="Fewer backlogs"><Minus size={14} /></button>
                    <input className="input text-center" style={{ width: 64 }} value={backlogs} inputMode="numeric" onChange={(e) => setBacklogs(Math.max(0, Number(e.target.value) || 0))} />
                    <button type="button" className="btn btn-outline btn-icon" onClick={() => setBacklogs((n) => n + 1)} aria-label="More backlogs"><Plus size={14} /></button>
                  </div>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="label">Class 10 score <span className="opt">(optional)</span></label><input className="input" inputMode="decimal" placeholder="9.5 CGPA or 94%" value={tenth} onChange={(e) => setTenth(e.target.value)} /></div>
                <div><label className="label">Class 12 / Diploma score <span className="opt">(optional)</span></label><input className="input" inputMode="decimal" placeholder="96.2%" value={twelfth} onChange={(e) => setTwelfth(e.target.value)} /></div>
              </div>
            </section>
          )}

          {/* STEP 4 — review */}
          {step === 4 && (
            <section className="grid gap-5">
              <div>
                <h1 className="h-page">Almost there</h1>
                <p className="subhead mt-2">Check what we&apos;re about to send.</p>
              </div>
              <div className="card card-pad grid gap-2 text-sm">
                <ReviewRow label="Name" value={name} />
                <ReviewRow label="Email" value={email} />
                <ReviewRow label="Mobile" value={mobile} />
                {dob && <ReviewRow label="Date of birth" value={dob + (isMinor ? ' (minor — parental consent needed)' : '')} />}
                <ReviewRow
                  label="Affiliation"
                  value={
                    affiliation === 'unaffiliated'
                      ? 'Not affiliated — Kollez Public only'
                      : affiliation === 'graduated'
                        ? 'Graduated'
                        : 'Currently studying'
                  }
                />
                {picked && (
                  <ReviewRow
                    label="College"
                    value={
                      <>
                        {picked.name}
                        {picked.breadcrumb && <span className="text-ink-tertiary"> · {picked.breadcrumb}</span>}
                        {picked.source === 'aishe' && (
                          <span className="badge ml-2" style={{ fontSize: 10 }}>AISHE — cold declare</span>
                        )}
                      </>
                    }
                  />
                )}
                {!skipAcademics && (
                  <>
                    <ReviewRow label="Program" value={`${program} · ${branch}`} />
                    <ReviewRow label="Joining year" value={`${joiningYear} (Year ${acYear})`} />
                    {roll && <ReviewRow label="Roll" value={roll} />}
                    {uniqueId && picked?.source === 'kollez' && (
                      <ReviewRow label="Roster identifier" value={uniqueId} />
                    )}
                  </>
                )}
              </div>

              {picked?.source === 'aishe' && (
                <Notice tone="info">
                  Your college isn&apos;t on Kollez yet. You&apos;ll start on Kollez Public — public
                  catalog, prep, resume, career advisor — and we&apos;ll notify you when your college
                  joins.
                </Notice>
              )}
              {picked?.source === 'kollez' && uniqueId.trim() && (
                <Notice tone="info">
                  We&apos;ll check your college&apos;s roster for a match. If we find one, we&apos;ll
                  send verification codes to the email and mobile they have on file — never yours.
                  That protects the roster from impersonation.
                </Notice>
              )}

              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" className="check mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>
                  I agree to Kollez processing my details as described in the{' '}
                  <Link className="link" to="/privacy" target="_blank">DPDP-compliant privacy notice</Link>.
                  I can withdraw consent later from Settings → Privacy.
                </span>
              </label>
            </section>
          )}

          {/* STEP 5 — DONE. Branches on the response shape. */}
          {step === 5 && path3 && (
            <ClaimOtpsStep
              claimAttemptId={path3.claimAttemptId}
              userId={path3.user.id}
              expiresInSec={path3.expiresInSec}
              otpsForDev={path3.otpsForDev}
              onSuccess={() => nav('/home', { replace: true })}
              onFallbackToPath2={() => {
                setPath3(null);
                setStep(4);
                void submitAll({ forcePath2: true });
              }}
            />
          )}
          {step === 5 && !path3 && immediateSuccess && (
            <section className="grid gap-5">
              <div className="ruled relative rounded-lg border border-line-tertiary bg-primary px-8 py-10 pl-14">
                <div className="absolute bottom-0 left-8 top-0 w-[2px]" style={{ background: 'rgb(var(--margin))' }} aria-hidden="true" />
                <MailCheck size={26} className="text-ink-success" />
                <h1 className="h-page mt-4">You&apos;re in</h1>
                <p className="subhead mt-2">
                  {picked
                    ? picked.source === 'aishe'
                      ? `We've noted ${picked.name}. You're starting on Kollez Public.`
                      : `Your college, ${picked.name}, will verify your profile from their end. In the meantime you can browse and apply.`
                    : `Kollez Public is ready for you.`}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Link className="btn btn-primary" to="/home">Go to my home</Link>
                  <Link className="btn btn-ghost" to="/onboarding">Set up my profile</Link>
                </div>
              </div>
              <div className="notice notice-info">
                <ShieldCheck size={16} />
                <span>
                  <strong>Signed in as {immediateSuccess.user.fullName}.</strong> You can switch between
                  workspaces from the top-left of your dashboard.
                </span>
              </div>
            </section>
          )}
        </div>
      </main>

      {step <= max && (
        <div className="wizard-foot">
          <div className="mx-auto flex w-full items-center gap-2" style={{ maxWidth: 640 }}>
            {step > 1 && (
              <button className="btn btn-ghost" type="button" onClick={back}>
                <ArrowLeft size={14} />Back
              </button>
            )}
            <span className="helper hidden sm:inline ml-auto">Progress saved on this device</span>
            <button className="btn btn-primary btn-lg" type="button" onClick={next} disabled={submitting}>
              {step === max ? (submitting ? 'Creating…' : 'Create my account') : 'Continue'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="text-ink-tertiary" style={{ minWidth: 140 }}>{label}</div>
      <div className="flex-1">{value}</div>
    </div>
  );
}

// Kept only to silence "unused" lints for icons wired into the ruled success card / notices.
void CircleAlert;
void FileUp;
void Search;
